import p5 from "p5";

export class DistortApp {
  private p?: typeof p5;
  private container: HTMLElement | null = null;
  private fontLoaded = false;

  initialize(): void {
    this.container = document.getElementById("p5-container");
    if (!this.container) throw new Error("#p5-container が見つかりません");

    const sketch = (p: typeof p5) => {
      /*** === 設定値 === ***/
      const TEXTS = ["Transition01", "Transition02", "END"];
      const STAGGER = 0.08;
      const SPEED = 0.01;
      const LINE = 205;
      const STEP = 200;
      const SPACING = 4;
      const EASING = (x: number) => x * x * x * (x * (x * 6 - 15) + 10);
      const EASEOUT = (x: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, x)), 3);

      /*** 🎨 カラー設定（setup後に初期化） ***/
      let COLORS: {
        background: typeof p5.Color;
        textPhase1: typeof p5.Color;
        textPhase2: typeof p5.Color;
        textPhase3: typeof p5.Color;
        spiralWhite: typeof p5.Color;
        spiralBlack: typeof p5.Color;
      };

      /*** === 状態 === ***/
      let phase = 0;
      let progressW = 0;
      let progressB = 0;
      let startTimes: number[] = [];
      let layouts: { [key: string]: { xs: number[]; width: number } } = {};
      let ready = false;

      // --- ねじり制御用 ---
      let spinOffset = 0;
      let targetSpin = 0;

      // --- フェーズ4用背景保持 ---
      let finalBgColor: p5.Color | null = null;

      /*** === フォント＆レイアウト === ***/
      const initFont = async () => {
        await this.ensureExoFont();
        p.textFont("Exo");
        this.fontLoaded = true;
        initLayouts();
        startTimes = [p.millis(), 0, 0];
        ready = true;
      };

      const initLayouts = () => {
        p.textAlign(p.CENTER, p.BASELINE);
        p.textSize(96);
        for (const t of TEXTS) {
          let x = 0;
          const xs = t.split("").map((ch) => {
            const pos = x;
            x += p.textWidth(ch) + SPACING;
            return Math.round(pos);
          });
          layouts[t] = { xs, width: Math.round(x - SPACING) };
        }
      };

      /*** === テキスト描画 === ***/
      const drawText = (text: string, start: number, color: p5.Color, fade = true) => {
        const layout = layouts[text];
        const baseY = (p.textAscent() - p.textDescent()) / 2;
        const totalW = layout.width;
        const startX = -totalW / 2;

        for (let i = 0; i < text.length; i++) {
          const u = fade ? (p.millis() - (start + i * 80)) / 500 : 1;
          const e = fade ? EASEOUT(u) : 1;
          if (e <= 0) continue;
          const alpha = 255 * e;
          const x = startX + layout.xs[i] + p.textWidth(text[i]) / 2;
          p.fill(p.red(color), p.green(color), p.blue(color), alpha);
          p.text(text[i], x, baseY);
        }
      };

      /*** === 渦描画 === ***/
      const renderSpiral = (col: typeof p5.Color, fillCenter: boolean, prog: number) => {
        const N = 13;
        const TURN = p.radians(30);
        const BIAS = 0.2;
        const RGAIN = 5;
        const MIN = 0.02;

        for (let i = N - 1; i >= 0; i--) {
          const cp = p.constrain(prog - (N - 1 - i) * STAGGER, 0, 1);
          if (cp <= 0) continue;

          const e = EASING(cp);
          const ang = e * p.TWO_PI;
          if (ang < MIN) continue;

          const dia = (i + 1) * STEP + RGAIN * ang;
          const baseAng = -p.HALF_PI + i * TURN + BIAS * ang + spinOffset;

          p.push();
          if (i === 0 && fillCenter) {
            p.fill(col);
            p.noStroke();
            p.arc(0, 0, dia, dia, baseAng, baseAng + ang, p.PIE);
          } else {
            p.noFill();
            p.stroke(col);
            p.strokeWeight(LINE);
            p.strokeCap(p.SQUARE);
            p.arc(0, 0, dia, dia, baseAng, baseAng + ang);
          }
          p.pop();
        }
      };

      /*** === setup === ***/
      p.setup = () => {
        p.createCanvas(p.windowWidth, p.windowHeight).parent(this.container!);
        p.pixelDensity(1);
        p.textAlign(p.CENTER, p.BASELINE);
        p.textSize(96);

        // 🟢 setup内で安全にp.color()を使う
        COLORS = {
          background: p.color(0),
          textPhase1: p.color(255),
          textPhase2: p.color(0),
          textPhase3: p.color(255),
          spiralWhite: p.color(255),
          spiralBlack: p.color(53, 255, 238), // 水色の渦
        };

        initFont();
      };

      /*** === draw === ***/
      p.draw = () => {
        if (!ready) return;

        // フェーズ4に入るまでは背景リセット
        if (phase < 4) p.background(COLORS.background);
        p.translate(p.width / 2, p.height / 2);

        // --- ホバー位置からねじれを算出 ---
        const dx = p.mouseX - p.width / 2;
        const dy = p.mouseY - p.height / 2;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angleToMouse = Math.atan2(dy, dx);
        const intensity = p.map(dist, 0, p.width / 2, 0, 1, true);
        targetSpin = angleToMouse * intensity * 0.5;
        spinOffset = p.lerp(spinOffset, targetSpin, 0.05);

        const totalProg = 1 + (10 - 1) * STAGGER;

        switch (phase) {
          case 0:
            drawText(TEXTS[0], startTimes[0], COLORS.textPhase1);
            if (p.millis() - startTimes[0] > TEXTS[0].length * 80 + 900) phase = 1;
            break;

          case 1:
            drawText(TEXTS[0], startTimes[0], COLORS.textPhase1, false);
            renderSpiral(COLORS.spiralWhite, true, progressW);
            progressW += SPEED;
            if (progressW > totalProg + 0.5) {
              phase = 2;
              startTimes[1] = p.millis();
            }
            break;

          case 2:
            renderSpiral(COLORS.spiralWhite, true, totalProg + 1);
            drawText(TEXTS[1], startTimes[1], COLORS.textPhase2);
            if (p.millis() - startTimes[1] > TEXTS[1].length * 80 + 900) {
              phase = 3;
              progressB = 0;
            }
            break;

          case 3:
            renderSpiral(COLORS.spiralWhite, true, totalProg + 1);
            drawText(TEXTS[1], startTimes[1], COLORS.textPhase2, false);
            renderSpiral(COLORS.spiralBlack, true, progressB);
            progressB += SPEED;
            if (progressB > totalProg + 0.5) {
              // フェーズ4突入時に背景色を保存
              finalBgColor = COLORS.spiralBlack;
              phase = 4;
              startTimes[2] = p.millis();
            }
            break;

          case 4:
            // 背景は塗り直さず、そのまま上にテキストを描く
            if (finalBgColor) p.background(finalBgColor);
            drawText(TEXTS[2], startTimes[2], COLORS.textPhase3);
            break;
        }
      };

      /*** === イベント === ***/
      p.windowResized = () => p.resizeCanvas(p.windowWidth, p.windowHeight);
      p.mousePressed = () => (
        (phase = 0),
        (progressW = 0),
        (progressB = 0),
        (startTimes[0] = p.millis()),
        (finalBgColor = null)
      );
    };

    this.p = new p5(sketch);
  }

  /*** === フォント読み込み === ***/
  private ensureExoFont(): Promise<void> {
    const head = document.head;
    const id = "gf-exo";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Exo:wght@300;600;900&display=swap";
      head.appendChild(link);
    }
    const anyDoc = document as any;
    return anyDoc.fonts?.load
      ? anyDoc.fonts.load('96px "Exo"').then(() => void 0)
      : Promise.resolve();
  }

  destroy(): void {
    this.p?.remove();
    this.p = undefined;
  }
}
